import { Navigate, Outlet, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { invalidateClientSession } from "@/shared/utils/authSession.util";
import { useAuth } from "@/hooks/useAuth";
import { RouteFallback } from "@/routes/RouteFallback";

type DecodedToken = {
  id: string;
  email: string;
  role: "ADMIN" | "CLIENT" | "FACILITATOR" | "MANAGER" | "EXPERT" | "ECOSYSTEM_HEAD" | "SUB_ADMIN";
  exp: number;
  iat: number;
};

export function FacilitatorProtectedRoute() {
  const location = useLocation();
  const { token, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <RouteFallback />;
  }

  if (!isAuthenticated || !token) {
    return <Navigate to="/facilitator/login" state={{ from: location }} replace />;
  }

  try {
    const decoded = jwtDecode<DecodedToken>(token);

    if (decoded.role === "ADMIN" || decoded.role === "SUB_ADMIN") {
      return <Navigate to="/admin/companies" replace />;
    }

    if (decoded.role === "MANAGER") {
      return <Navigate to="/management" replace />;
    }

    if (decoded.role === "CLIENT") {
      return <Navigate to="/dashboard" replace />;
    }

    if (decoded.role === "EXPERT") {
      return <Navigate to="/expert/dashboard" replace />;
    }

    if (decoded.role === "ECOSYSTEM_HEAD") {
      return <Navigate to="/ecosystem-head/dashboard" replace />;
    }

    if (decoded.role !== "FACILITATOR") {
      invalidateClientSession();
      return <Navigate to="/facilitator/login" state={{ from: location }} replace />;
    }

    return <Outlet />;
  } catch {
    invalidateClientSession();
    return <Navigate to="/facilitator/login" state={{ from: location }} replace />;
  }
}
